import { ClimatePreset, ContactStatus, Segment, SiteSort, Tier } from "@plenum/shared/api";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { z } from "zod";

/**
 * URL search state shared by the map (`/`) and table (`/table`) routes (PRD §12.2: "all URL-synced").
 * Filters mirror `SiteFilters` in packages/shared; view state (drawer, basemap, camera) is added here.
 * Arrays travel as comma lists; values equal to their default are dropped from the URL.
 */

const csv = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") return v === "" ? [] : v.split(",").map((s) => s.trim());
    return undefined;
  }, z.array(item).optional());

const bool = z.preprocess((v) => {
  if (typeof v === "boolean") return v;
  if (v === "1" || v === "true") return true;
  if (v === "0" || v === "false") return false;
  return undefined;
}, z.boolean().optional());

const optNum = (min: number, max: number) =>
  z.preprocess((v) => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }, z.number().min(min).max(max).optional());

export const DEFAULTS = {
  mw_min: 40,
  mw_max: 130,
  page: 1,
  per_page: 50,
  basemap: "positron" as const,
  osm: true,
};

export const SearchSchema = z.object({
  // filters (PRD §12.2)
  mw_min: optNum(0, 2000).default(DEFAULTS.mw_min),
  mw_max: optNum(0, 2000).default(DEFAULTS.mw_max),
  core: bool,
  pm: csv(z.string().max(4)),
  frame: csv(z.string().max(20)),
  oem: csv(z.string().max(40)),
  state: csv(z.string().length(2)),
  ba: csv(z.string().max(8)),
  sector: csv(z.coerce.number().int().min(1).max(7)),
  preset: ClimatePreset.optional().catch(undefined),
  tier: csv(Tier),
  cooling: csv(z.string().max(32)),
  contact: csv(ContactStatus),
  operator: z.string().max(120).optional(),
  year_min: optNum(1900, 2100),
  year_max: optNum(1900, 2100),
  status: csv(z.string().max(12)),
  mothballed: bool,
  segment: csv(Segment),
  bbm_ref: bool,
  q: z.string().max(120).optional(),
  list: optNum(1, 1e9),
  sort: SiteSort.optional().catch(undefined),
  dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  page: optNum(1, 1e6).default(DEFAULTS.page),
  per_page: optNum(1, 200).default(DEFAULTS.per_page),
  // view state
  site: optNum(1, 1e9),
  tab: z.string().max(20).optional(),
  sat: bool,
  basemap: z.enum(["positron", "liberty"]).optional().catch(undefined),
  osm: bool,
  c: z
    .preprocess((v) => {
      if (typeof v !== "string") return v;
      const parts = v.split(",").map(Number);
      return parts.length === 2 && parts.every(Number.isFinite) ? parts : undefined;
    }, z.tuple([z.number(), z.number()]).optional())
    .catch(undefined),
  z: optNum(0, 22),
});

export type Search = z.infer<typeof SearchSchema>;
export type SearchInput = Partial<z.input<typeof SearchSchema>>;
export type SearchPatch = Partial<Search>;

export function parseSearch(raw: SearchInput & SearchSchemaInput): Search {
  const res = SearchSchema.safeParse(raw);
  if (res.success) return res.data;
  // Fall back field by field so one bad value never wipes the rest of the URL.
  const cleaned: Record<string, unknown> = { ...raw };
  for (const issue of res.error.issues) {
    const key = issue.path[0];
    if (typeof key === "string") delete cleaned[key];
  }
  return SearchSchema.parse(cleaned);
}

/** Keys that describe the result set, in the order the API expects. */
export const FILTER_KEYS = [
  "mw_min",
  "mw_max",
  "core",
  "pm",
  "frame",
  "oem",
  "state",
  "ba",
  "sector",
  "preset",
  "tier",
  "cooling",
  "contact",
  "operator",
  "year_min",
  "year_max",
  "status",
  "mothballed",
  "segment",
  "bbm_ref",
  "q",
  "list",
] as const satisfies readonly (keyof Search)[];

export type FilterKey = (typeof FILTER_KEYS)[number];

/** PRD §12.1: the default map view is tiers 1 and 2 once scores exist (docs/questions.md Q15). */
export const DEFAULT_TIERS: Search["tier"] = ["1", "2"];

/**
 * Apply the tier default: when the universe has scores and the URL names no tier, the result set
 * is tiers 1 and 2. An explicit `tier` (any subset, including all four) always wins, so "any"
 * is reachable by selecting every tier.
 */
export function withTierDefault<T extends { tier?: Search["tier"] }>(
  filters: T,
  hasScores: boolean,
): T {
  if (!hasScores || (filters.tier && filters.tier.length > 0)) return filters;
  return { ...filters, tier: DEFAULT_TIERS };
}

/** Filter-only subset, used as the query key and the API query string. */
export function filterPart(s: Search): Pick<Search, FilterKey> {
  const out: Record<string, unknown> = {};
  for (const k of FILTER_KEYS) if (s[k] !== undefined) out[k] = s[k];
  return out as Pick<Search, FilterKey>;
}

/** Number of non-default filters set (for the rail header badge). */
export function activeFilterCount(s: Search): number {
  let n = 0;
  for (const k of FILTER_KEYS) {
    const v = s[k];
    if (v === undefined || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (k === "mw_min" && v === DEFAULTS.mw_min) continue;
    if (k === "mw_max" && v === DEFAULTS.mw_max) continue;
    n++;
  }
  return n;
}

export function toParams(values: Record<string, unknown>): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      p.set(k, v.map(String).join(","));
    } else if (v === true) {
      p.set(k, "1");
    } else {
      p.set(k, String(v));
    }
  }
  return p;
}

/** Router-level serializer: drops defaults, writes comma lists, keeps the URL readable. */
export function stringifySearch(search: Record<string, unknown>): string {
  const trimmed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(search)) {
    if (k in DEFAULTS && v === DEFAULTS[k as keyof typeof DEFAULTS]) continue;
    trimmed[k] = v;
  }
  const s = toParams(trimmed).toString();
  return s ? `?${s}` : "";
}

export function parseSearchString(raw: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const p = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  for (const [k, v] of p.entries()) out[k] = v;
  return out;
}
